import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Lock, BarChart3, TrendingUp, PieChart, LineChart, 
  Check, Star, Loader2, Sparkles
} from 'lucide-react';
import { subscriptionAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

const SubscriptionPaywall = ({ prices, onSubscribed }) => {
  const { toast } = useToast();
  const { getAuthToken } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState('analytics_monthly');
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const result = await subscriptionAPI.createCheckout(
        selectedPlan,
        window.location.origin,
        token
      );
      
      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      }
    } catch (error) {
      console.error('Subscription error:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to start subscription',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Drops a trailing ".00" so £12 stays "12" while £136.80 keeps both decimals.
  const formatPrice = (amount) => Number(amount).toFixed(2).replace(/\.00$/, '');

  const features = [
    { icon: BarChart3, text: 'Revenue analytics & insights' },
    { icon: TrendingUp, text: 'Occupancy rate tracking' },
    { icon: PieChart, text: 'Expense breakdown' },
    { icon: LineChart, text: 'Performance forecasting' },
    { icon: Star, text: 'Comparative market analysis' },
  ];

  return (
    <div className="min-h-[600px] flex items-center justify-center p-6">
      <div className="max-w-4xl w-full">
        {/* Lock Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#1F3A68]/10 rounded-full mb-4">
            <Lock className="h-8 w-8 text-[#1F3A68]" />
          </div>
          <h2 className="text-3xl font-bold text-[#1F3A68] mb-2">
            Unlock Analytics Pro
          </h2>
          <p className="text-gray-600 max-w-lg mx-auto">
            Get powerful insights into your rental business performance with our comprehensive analytics suite.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-5 gap-4 mb-8">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="flex flex-col items-center p-4 bg-white rounded-lg border border-gray-100 shadow-sm"
            >
              <feature.icon className="h-6 w-6 text-[#1F3A68] mb-2" />
              <span className="text-xs text-center text-gray-600">{feature.text}</span>
            </div>
          ))}
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
          {/* Monthly Plan */}
          <Card 
            className={`cursor-pointer transition-all ${
              selectedPlan === 'analytics_monthly' 
                ? 'border-2 border-[#1F3A68] shadow-lg' 
                : 'border border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => setSelectedPlan('analytics_monthly')}
            data-testid="plan-monthly"
          >
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Monthly</CardTitle>
                {selectedPlan === 'analytics_monthly' && (
                  <div className="h-5 w-5 rounded-full bg-[#1F3A68] flex items-center justify-center">
                    <Check size={12} className="text-white" />
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-[#1F3A68] mb-1">
                £{formatPrice(prices?.analytics_monthly?.amount || 12)}
                <span className="text-sm font-normal text-gray-500">/month</span>
              </div>
              <p className="text-sm text-gray-600">Cancel anytime</p>
            </CardContent>
          </Card>

          {/* Yearly Plan */}
          <Card 
            className={`cursor-pointer transition-all relative ${
              selectedPlan === 'analytics_yearly' 
                ? 'border-2 border-[#1F3A68] shadow-lg' 
                : 'border border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => setSelectedPlan('analytics_yearly')}
            data-testid="plan-yearly"
          >
            <Badge className="absolute -top-2 -right-2 bg-green-500">
              <Sparkles size={12} className="mr-1" />
              Save 5%
            </Badge>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Yearly</CardTitle>
                {selectedPlan === 'analytics_yearly' && (
                  <div className="h-5 w-5 rounded-full bg-[#1F3A68] flex items-center justify-center">
                    <Check size={12} className="text-white" />
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-[#1F3A68] mb-1">
                £{formatPrice(prices?.analytics_yearly?.amount || 136.8)}
                <span className="text-sm font-normal text-gray-500">/year</span>
              </div>
              <p className="text-sm text-gray-600">Billed annually</p>
            </CardContent>
          </Card>
        </div>

        {/* Subscribe Button */}
        <div className="text-center mt-8">
          <Button 
            size="lg"
            onClick={handleSubscribe}
            disabled={loading}
            className="bg-[#1F3A68] hover:bg-[#1F3A68]/90 px-12"
            data-testid="subscribe-btn"
          >
            {loading ? (
              <>
                <Loader2 size={18} className="mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                Subscribe Now
              </>
            )}
          </Button>
          <p className="text-xs text-gray-500 mt-3">
            Secure payment powered by Stripe
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPaywall;
